import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Itly from 'itly';
import SvgContainer from 'au-gatsby-ds/components/Svg';
import { LoadingSpinnerIcon } from 'au-gatsby-ds/components/LoadingSpinnerIcon/styled';
import {
  ThumbnailContainer,
  RightSideContainer,
  StickyCTAContainer,
  ButtonText,
  CTAButton,
  ExpandButton,
  ImageBottomBorder
} from './styled';
import { useProductLivePreview } from '../../../hooks/useProductLivePreview';
import { useMedia } from '../../../hooks/useMedia';
import { useProductForm } from 'features/pdp/hooks/useProductForm';
import { StickyCTAMessaging } from './StickyCTAMessaging';
import { ShowEnabledButton } from './ShowEnabledButton';
import { scrollIntoFirstErrorInput } from 'features/pdp/helpers';
import { useProduct } from 'features/pdp/hooks/useProduct';
import { openLivePreviewModal as _openLivePreviewModal } from 'store/modal/actions';
import { convertToReportingProduct } from 'analytics/itly/helpers/product';

type StickyCTAContainerProps = {
  price?: number;
  crossedOutPrice?: number;
};

export const StickyCTA = ({ price, crossedOutPrice }: StickyCTAContainerProps): JSX.Element => {
  const { form, inputs, isSubmitSuccessful, handleSubmit } = useProductForm();
  const [containerVisibility, setContainerVisibility] = useState<'show'>(undefined);
  const [stickyCtaImpressionFired, setStickyCtaImpressionFired] = useState(false);

  const livePreviewUrl = useProductLivePreview();
  const { product } = useProduct();
  const { photos } = useMedia();

  const ctaButtonOnVisible = useCallback(() => {
    if (!stickyCtaImpressionFired) {
      setStickyCtaImpressionFired(true);
      Itly.ctaImpression(convertToReportingProduct(product));
    }
  }, [stickyCtaImpressionFired, product]);

  const showAfterScrolling = 300;
  const thumbnailUrl = useMemo(() => livePreviewUrl || (photos && photos[0].thumb), [livePreviewUrl, photos]);

  const ctaLabel = form.getValues('has_prints') === '1' ? 'Create Your Prints' : product.addToCartLabel;

  const firstErrorInputId = useMemo(() => {
    const errorsInputIds = Object.keys(form.formState.errors);
    const findErrorByInputId = inputId => errorsInputIds?.find(errorInput => errorInput === inputId);
    return inputs.find(input => findErrorByInputId(input.id))?.id;
  }, [form, inputs]);

  useEffect(() => {
    scrollIntoFirstErrorInput(firstErrorInputId);
  }, [form.formState.submitCount, firstErrorInputId]);

  useEffect(() => {
    if (livePreviewUrl && window.scrollY > showAfterScrolling) {
      setContainerVisibility('show');
      setTimeout(() => setContainerVisibility(undefined), 500);
    }
  }, [livePreviewUrl]);

  const dispatch = useDispatch();

  const handleExpandLivePreview = (): void => {
    Itly.ctaPreviewExpanded(convertToReportingProduct(product));
    dispatch(_openLivePreviewModal());
  };

  return (
    <StickyCTAContainer
      id="sticky-cta-container"
      position="bottom"
      showAfterScrolling={0}
      tempVisibility={containerVisibility}
      onVisible={ctaButtonOnVisible}
    >
      <ThumbnailContainer onClick={handleExpandLivePreview}>
        <ExpandButton id="expand-live-preview">
          <SvgContainer icon="expand"/>
        </ExpandButton>
        <img src={thumbnailUrl} alt="product-preview-thumbnail"/>
        <ImageBottomBorder/>
      </ThumbnailContainer>
      <RightSideContainer>
        <StickyCTAMessaging content={product.stickyCTAMessaging}/>
        <CTAButton
          id="sticky-cta-button"
          onClick={handleSubmit}
          disabled={isSubmitSuccessful}
        >
          {isSubmitSuccessful ? (
              <>
                <LoadingSpinnerIcon/>
                <ButtonText className="disabled">
                  {ctaLabel === 'ADD TO CART' ? 'Adding To Cart' : 'Loading The Editor'}
                </ButtonText>
              </>
            )
            : (
              <ShowEnabledButton buttonLabel={ctaLabel} price={price} crossedOutPrice={crossedOutPrice}/>
            )}
        </CTAButton>
      </RightSideContainer>
    </StickyCTAContainer>
  );
};
